import React from 'react';
import { Shield, Download, ExternalLink, ChevronRight } from 'lucide-react';
import LevelUpHeader from '../components/LevelUpHeader';
import LevelUpFooter from '../components/LevelUpFooter';

/* ── Google Fonts: Inter ─────────────────────────────────────────────────── */
const interFontLink = document.getElementById('inter-font');
if (!interFontLink) {
  const link = document.createElement('link');
  link.id = 'inter-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
  document.head.appendChild(link);
}

/* ── Design tokens ───────────────────────────────────────────────────────── */
const FONT      = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const HERO_BG   = 'linear-gradient(135deg, #1c4b50 0%, #11303a 100%)';
const BADGE_BG  = '#1c4b50';       // section number badge
const ACCENT    = '#1c4b50';       // links, buttons
const ACCENT_LT = '#00A38A';       // email/link accent
const PAGE_BG   = '#F5F0EA';       // warm cream outer bg
const CARD_BG   = '#FFFFFF';       // white inner card
const DIVIDER   = '#E8E6E0';
const BODY      = '#2D2D2D';
const MUTED     = '#6B7280';
const TRUST_LABEL_COLOR = '#C5E566'; // lime/chartreuse for hero label
const CARD_BORDER = '#D1EAE6';      // mint teal for data cards
const CARD_FILL   = '#F0FAF8';      // very light mint fill for data cards
const NOTE_BG     = '#F3FAE6';
const NOTE_BORDER = '#C9E870';

const PDF_URL = 'https://technofocus.ai/privacy';

/* ── Primitive components ─────────────────────────────────────────────────── */

/** Thin section divider */
function Divider() {
  return <hr style={{ border: 'none', borderTop: `1px solid ${DIVIDER}`, margin: '16px 0 20px' }} />;
}

/** Numbered section badge — dark teal rounded square */
function Badge({ number }) {
  return (
    <div style={{
      flexShrink: 0,
      width: '34px', height: '34px',
      borderRadius: '8px',
      backgroundColor: BADGE_BG,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px', fontFamily: FONT }}>
        {number}
      </span>
    </div>
  );
}

/** Section wrapper */
function Section({ number, title, children }) {
  return (
    <section style={{ paddingTop: '32px' }} data-testid={`section-${number}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <Badge number={number} />
        <h2 style={{ fontFamily: FONT, fontWeight: 600, fontSize: '22px', color: BODY, margin: 0, lineHeight: 1.3 }}>
          {title}
        </h2>
      </div>
      <Divider />
      <div>{children}</div>
    </section>
  );
}

/** Body paragraph */
function P({ children, style }) {
  return (
    <p style={{ fontFamily: FONT, fontWeight: 400, fontSize: '15px', color: BODY, lineHeight: 1.7, margin: '0 0 12px', ...style }}>
      {children}
    </p>
  );
}

/** Bullet list */
function Ul({ items }) {
  return (
    <ul style={{ margin: '4px 0 12px 6px', padding: 0 }}>
      {items.map((item, i) => (
        <li key={i} style={{
          fontFamily: FONT, fontWeight: 400, fontSize: '15px', color: BODY,
          lineHeight: 1.7, marginBottom: '2px',
          listStyle: 'disc', marginLeft: '20px',
        }}>{item}</li>
      ))}
    </ul>
  );
}

/** Sub-heading inside a section */
function SubH({ children }) {
  return (
    <p style={{ fontFamily: FONT, fontWeight: 700, fontSize: '15px', color: BODY, margin: '20px 0 4px' }}>
      {children}
    </p>
  );
}

/** Note callout — yellow-green */
function Note({ children }) {
  return (
    <div style={{
      backgroundColor: NOTE_BG,
      border: `1px solid ${NOTE_BORDER}`,
      borderRadius: '8px',
      padding: '12px 18px',
      marginTop: '16px',
      fontFamily: FONT,
      fontSize: '14px',
      color: BODY,
      lineHeight: 1.6,
    }}>
      <strong>Note: </strong>{children}
    </div>
  );
}

/** Version history row */
function VersionHistoryBar() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '14px 20px',
      border: `1px solid ${DIVIDER}`,
      borderRadius: '10px',
      marginBottom: '8px',
    }}>
      <span style={{
        fontFamily: FONT, fontWeight: 600, fontSize: '12px',
        letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED,
      }}>
        Version History
      </span>
      {/* v2.1 pill in dark teal */}
      <span style={{
        backgroundColor: BADGE_BG, color: '#fff',
        fontFamily: FONT, fontWeight: 600, fontSize: '12px',
        padding: '3px 10px', borderRadius: '20px',
        letterSpacing: '0.02em',
      }}>
        v2.1
      </span>
      <ChevronRight size={14} color={MUTED} style={{ marginLeft: 'auto' }} />
    </div>
  );
}

/** Company info card — light mint, key-value rows, no harsh borders */
function InfoCard({ rows }) {
  return (
    <div style={{
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: '10px',
      overflow: 'hidden',
      marginTop: '12px',
    }}>
      {rows.map(([label, value, isLink], i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '220px 1fr',
          padding: '14px 20px',
          borderBottom: i < rows.length - 1 ? `1px solid ${CARD_BORDER}` : 'none',
          backgroundColor: CARD_BG,
        }}>
          <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: '14px', color: BODY }}>{label}</span>
          {isLink
            ? <a href={`mailto:${value}`} style={{ fontFamily: FONT, fontWeight: 600, fontSize: '14px', color: ACCENT, textDecoration: 'none' }}>{value}</a>
            : <span style={{ fontFamily: FONT, fontWeight: 400, fontSize: '14px', color: BODY }}>{value}</span>
          }
        </div>
      ))}
    </div>
  );
}

/** Data category card (2-col grid for section 4) */
function DataCard({ title, items }) {
  return (
    <div style={{
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: '10px',
      padding: '16px 20px',
      backgroundColor: CARD_BG,
    }}>
      <p style={{
        fontFamily: FONT, fontWeight: 600, fontSize: '12px',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        color: ACCENT, margin: '0 0 10px',
      }}>
        {title}
      </p>
      <ul style={{ margin: 0, padding: 0 }}>
        {items.map((item, i) => (
          <li key={i} style={{
            fontFamily: FONT, fontSize: '14px', color: BODY,
            lineHeight: 1.7, marginBottom: '2px',
            listStyle: 'disc', marginLeft: '18px',
          }}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/** Sharing entity row */
function SharingRow({ label, value }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '240px 1fr',
      alignItems: 'center',
      padding: '14px 20px',
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: '10px',
      marginBottom: '8px',
      backgroundColor: CARD_BG,
    }}>
      <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: '14px', color: BODY }}>{label}</span>
      <span style={{ fontFamily: FONT, fontWeight: 400, fontSize: '14px', color: MUTED }}>{value}</span>
    </div>
  );
}

/** Country pill */
function CountryPill({ label }) {
  return (
    <div style={{
      padding: '10px 24px',
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: '8px',
      fontFamily: FONT, fontWeight: 600, fontSize: '14px', color: BODY,
      backgroundColor: CARD_BG,
    }}>
      {label}
    </div>
  );
}

/** Retention examples table */
function RetentionTable({ rows }) {
  return (
    <div style={{ border: `1px solid ${CARD_BORDER}`, borderRadius: '10px', overflow: 'hidden', marginTop: '12px' }}>
      <div style={{
        backgroundColor: CARD_FILL,
        padding: '10px 20px',
        borderBottom: `1px solid ${CARD_BORDER}`,
      }}>
        <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: ACCENT }}>
          Examples
        </span>
      </div>
      {rows.map(([label, value], i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '220px 1fr',
          padding: '13px 20px',
          borderBottom: i < rows.length - 1 ? `1px solid ${CARD_BORDER}` : 'none',
          backgroundColor: CARD_BG,
        }}>
          <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: '14px', color: BODY }}>{label}</span>
          <span style={{ fontFamily: FONT, fontWeight: 400, fontSize: '14px', color: MUTED }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

/** "Compliance and Protection" sub-box inside section 5 */
function SubBox({ title, intro, items }) {
  return (
    <div style={{
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: '10px',
      padding: '16px 20px',
      marginTop: '16px',
      backgroundColor: CARD_BG,
    }}>
      <p style={{ fontFamily: FONT, fontWeight: 700, fontSize: '14px', color: BODY, margin: '0 0 6px' }}>{title}</p>
      <P style={{ marginBottom: '6px' }}>{intro}</P>
      <Ul items={items} />
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────────── */
export default function PrivacyPolicy({ user, onLogout }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: PAGE_BG, fontFamily: FONT }}>
      <LevelUpHeader user={user} onLogout={onLogout} />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div style={{ background: HERO_BG, padding: '44px 48px 52px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>

          {/* Breadcrumb */}
          <p style={{ fontFamily: FONT, fontSize: '13px', color: 'rgba(255,255,255,0.55)', margin: '0 0 28px' }}>
            Home / Trust Center / <span style={{ color: 'rgba(255,255,255,0.85)' }}>Privacy Policy</span>
          </p>

          {/* TRUST & PRIVACY label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '20px' }}>
            <Shield size={13} color={TRUST_LABEL_COLOR} />
            <span style={{
              fontFamily: FONT, fontWeight: 600, fontSize: '12px',
              color: TRUST_LABEL_COLOR,
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>
              Trust &amp; Privacy
            </span>
          </div>

          {/* Page title + Download PDF aligned */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
            <div>
              <h1 style={{
                fontFamily: FONT, fontWeight: 700, fontSize: '52px', color: '#FFFFFF',
                margin: '0 0 16px', lineHeight: 1.1, letterSpacing: '-0.01em',
              }}>
                Privacy Policy
              </h1>
              <p style={{ fontFamily: FONT, fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                Last Updated: 2 April 2026
              </p>
            </div>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-pdf-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '11px 22px',
                border: '1.5px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: '#FFFFFF',
                textDecoration: 'none',
                fontFamily: FONT, fontWeight: 500, fontSize: '14px',
                backgroundColor: 'rgba(255,255,255,0.08)',
                transition: 'background 0.2s, border-color 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
            >
              <Download size={15} />
              Download PDF
            </a>
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: '40px 48px 64px' }}>
        <div style={{
          maxWidth: '960px', margin: '0 auto',
          backgroundColor: CARD_BG,
          borderRadius: '16px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
          padding: '36px 44px 48px',
        }}>

          {/* Company meta */}
          <div style={{ marginBottom: '4px' }}>
            <p style={{ fontFamily: FONT, fontSize: '13px', color: MUTED, margin: '0 0 2px' }}>Technofocus Pte. Ltd.</p>
            <a href="mailto:privacy@technofocus.ai" style={{ fontFamily: FONT, fontWeight: 600, fontSize: '13px', color: ACCENT_LT, textDecoration: 'none' }}>
              privacy@technofocus.ai
            </a>
          </div>

          {/* Version History */}
          <div style={{ marginTop: '20px', marginBottom: '0' }}>
            <VersionHistoryBar />
          </div>

          {/* ── Section 1 ────────────────────────────────────────────── */}
          <Section number="1" title="Introduction">
            <P><strong>Technofocus Pte. Ltd.</strong> ("Technofocus", "we", "our", or "us") provides learning and enablement platforms, including:</P>
            <Ul items={[
              'LevelUp Learning Platform',
              'Compete Content Hub',
              'Skilling-in-a-Box',
              'Associated training, event, and enablement services (collectively, the "Services")',
            ]} />
            <P>This Privacy Policy explains how we collect, use, process, and protect personal data in connection with our Services.</P>
          </Section>

          {/* ── Section 2 ────────────────────────────────────────────── */}
          <Section number="2" title="Company Information">
            <P>Technofocus Pte. Ltd. is the entity responsible for the processing of personal data described in this Privacy Policy.</P>
            <InfoCard rows={[
              ['Registered Entity',     'Technofocus Pte. Ltd.',                                         false],
              ['Registered Address',    '531A, Hong Lim Complex, Upper Cross Street, Singapore 051531',  false],
              ['Operational Presence',  'Global, including Singapore and India',                         false],
              ['Contact Email',         'privacy@technofocus.ai',                                        true],
            ]} />
          </Section>

          {/* ── Section 3 ────────────────────────────────────────────── */}
          <Section number="3" title="Our Role in Data Processing">
            <P>In most cases, Technofocus acts as a <strong>data processor</strong> on behalf of customer organizations (such as employers, partners, or program sponsors), which act as the <strong>data controllers</strong>.</P>
            <P>In certain cases (such as direct registrations, communications, or marketing interactions), Technofocus may act as a <strong>data controller</strong>.</P>
          </Section>

          {/* ── Section 4 ────────────────────────────────────────────── */}
          <Section number="4" title="Personal Data We Collect">
            <P>We collect and process personal data necessary to deliver our Services, including:</P>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <DataCard title="Contact & Identity Data" items={['Name', 'Email address', 'Organization name', 'Job role']} />
              <DataCard title="Account & Authentication Data" items={['Login credentials (securely managed)', 'User ID and authentication-related data']} />
              <DataCard title="Learning & Usage Data" items={['Course enrollment and completion data', 'Platform usage and interaction data', 'Role-based access data']} />
              <DataCard title="Event & Transaction Data" items={['Event registration and participation data', 'Payment-related information (processed via secure third-party payment providers, where applicable)']} />
              <DataCard title="Communications Data" items={['Emails, support requests, feedback, and correspondence']} />
              <DataCard title="Device & Technical Data" items={['IP address', 'Device type', 'Browser type', 'Operating system', 'Approximate location']} />
            </div>
            <Note>We do <strong>not</strong> intentionally collect or process special categories of personal data.</Note>
          </Section>

          {/* ── Section 5 ────────────────────────────────────────────── */}
          <Section number="5" title="How We Use Personal Data">
            <P>We use personal data to:</P>
            <Ul items={[
              'Provide and operate our Services',
              'Manage enrollments and user accounts',
              'Track progress and completion',
              'Enable role-based access to content',
              'Deliver events, certifications, and services',
              'Provide reporting to sponsoring organizations',
              'Improve platform functionality and user experience',
              'Conduct research and development',
              'Communicate service updates, support, and administrative messages',
            ]} />
            <SubBox
              title="Compliance and Protection"
              intro="We may also use personal data to:"
              items={[
                'Comply with legal obligations',
                'Enforce our terms and policies',
                'Protect rights, safety, and security',
                'Detect, prevent, and investigate fraud or misuse',
              ]}
            />
          </Section>

          {/* ── Section 6 ────────────────────────────────────────────── */}
          <Section number="6" title="Sharing of Personal Data">
            <P>We may share personal data with:</P>
            <div style={{ marginTop: '8px' }}>
              <SharingRow label="Customer Organizations" value="Employers, partners, or program sponsors" />
              <SharingRow label="Program Partners" value="Including Microsoft and authorized collaboration platforms for reporting and enablement" />
              <SharingRow label="Service Providers (Sub-processors)" value="Infrastructure, LMS, labs, analytics, events, and related services" />
              <SharingRow label="Affiliates" value="Technofocus group entities for operational purposes" />
              <SharingRow label="Professional Advisors" value="Legal, financial, audit, and compliance advisors" />
              <SharingRow label="Authorities" value="Law enforcement or regulatory bodies where required" />
              <SharingRow label="Business Transfers" value="In connection with mergers, acquisitions, restructuring, or asset transfers" />
            </div>
          </Section>

          {/* ── Section 7 ────────────────────────────────────────────── */}
          <Section number="7" title="Sub-processors">
            <P>Technofocus engages trusted third-party service providers ("Sub-processors") to support the delivery of Services. A current list is available at:</P>
            <a
              href="https://technofocus.ai/sub-processors"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px',
                backgroundColor: BADGE_BG,
                borderRadius: '8px',
                color: '#fff',
                fontFamily: FONT, fontWeight: 600, fontSize: '14px',
                textDecoration: 'none',
                marginTop: '4px',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#11303a'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = BADGE_BG}
            >
              View Sub-Processor List
            </a>
          </Section>

          {/* ── Section 8 ────────────────────────────────────────────── */}
          <Section number="8" title="Data Hosting and Infrastructure">
            <P>Our core platform infrastructure is hosted on <strong>Microsoft Azure</strong> in the United States (West US and East US regions). Certain components (e.g., labs, content delivery, events, and applications) may operate on additional infrastructure providers under Technofocus control. Authentication is centrally managed, while certain modules store role-based access and usage data locally.</P>
          </Section>

          {/* ── Section 9 ────────────────────────────────────────────── */}
          <Section number="9" title="International Data Transfers">
            <P>Personal data may be transferred to and processed in jurisdictions outside the European Economic Area (EEA), including:</P>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '12px 0 16px' }}>
              {['United States', 'Singapore', 'India', 'United Kingdom'].map(c => <CountryPill key={c} label={c} />)}
            </div>
            <P>Such transfers are governed by:</P>
            <Ul items={[
              'Standard Contractual Clauses (SCCs)',
              'Contractual safeguards with service providers',
              'Industry-standard security measures',
            ]} />
            <P>Technofocus operates a unified global platform and does not maintain region-specific hosting environments.</P>
          </Section>

          {/* ── Section 10 ───────────────────────────────────────────── */}
          <Section number="10" title="Security of Personal Data">
            <P>We implement appropriate safeguards, including:</P>
            <Ul items={[
              'Encryption in transit (TLS)',
              'Encryption at rest (where supported)',
              'Role-based access controls',
              'Least-privilege access',
              'Monitoring and logging',
              'Secure cloud infrastructure',
            ]} />
          </Section>

          {/* ── Section 11 ───────────────────────────────────────────── */}
          <Section number="11" title="Data Retention">
            <P>We retain personal data only as long as necessary to deliver Services, fulfill contractual obligations, and comply with legal requirements.</P>
            <RetentionTable rows={[
              ['Account data',   'Retained while account is active'],
              ['Marketing data', 'Retained until opt-out'],
              ['Support queries','Retained for a limited period'],
            ]} />
          </Section>

          {/* ── Section 12 ───────────────────────────────────────────── */}
          <Section number="12" title="Your Rights">
            <P>Depending on applicable laws, you may have the right to:</P>
            <Ul items={[
              'Access your data',
              'Correct inaccuracies',
              'Request deletion',
              'Restrict or object to processing',
              'Request data portability',
            ]} />
            <P>Where Technofocus acts as a processor, requests should be directed to your organization.</P>
          </Section>

          {/* ── Section 13 ───────────────────────────────────────────── */}
          <Section number="13" title="Cookies and Tracking Technologies">
            <P>We use cookies and similar technologies to enable platform functionality, enhance user experience, and analyze usage patterns.</P>
            <P>Detailed information about the cookies we use, including categories, purposes, and how to manage your preferences, is available in our Cookie Policy:{' '}
              <a href="https://technofocus.ai/cookie-policy" target="_blank" rel="noopener noreferrer"
                style={{ color: ACCENT_LT, fontWeight: 500, textDecoration: 'underline' }}>
                technofocus.ai/cookie-policy
              </a>.
            </P>
          </Section>

          {/* ── Section 14 ───────────────────────────────────────────── */}
          <Section number="14" title="Analytics and Tracking Technologies">
            <P>We use analytics tools, including Tealium, to understand platform usage and improve performance. These tools may collect:</P>
            <Ul items={[
              'Page visits and navigation patterns',
              'Device and browser information',
              'Approximate location (based on IP)',
            ]} />
            <Note>Such tools operate only after user consent (where required) and data is processed in aggregated or pseudonymized form.</Note>
          </Section>

          {/* ── Section 15 ───────────────────────────────────────────── */}
          <Section number="15" title="Public Forums and User Content">
            <P>Information shared in public areas (e.g., forums, discussions) may be visible to others. Users are responsible for any information they choose to disclose.</P>
          </Section>

          {/* ── Section 16 ───────────────────────────────────────────── */}
          <Section number="16" title="Third-Party Services">
            <P>Our Services may link to or integrate with third-party platforms. These operate under their own privacy policies.</P>
          </Section>

          {/* ── Section 17 ───────────────────────────────────────────── */}
          <Section number="17" title="Do Not Track">
            <P>We do not currently respond to "Do Not Track" browser signals.</P>
          </Section>

          {/* ── Section 18 ───────────────────────────────────────────── */}
          <Section number="18" title="Updates to This Policy">
            <P>We may update this Privacy Policy from time to time. Updates will be posted with a revised "Last Updated" date.</P>
          </Section>

          {/* ── Section 19 ───────────────────────────────────────────── */}
          <Section number="19" title="Contact Us">
            <P>For questions regarding this Privacy Policy:</P>
            <div style={{
              border: `1px solid ${CARD_BORDER}`,
              borderRadius: '10px',
              padding: '18px 24px',
              marginBottom: '16px',
              backgroundColor: CARD_FILL,
            }}>
              <p style={{ fontFamily: FONT, fontWeight: 700, fontSize: '15px', color: BODY, margin: '0 0 4px' }}>Data Protection</p>
              <a href="mailto:privacy@technofocus.ai" style={{ fontFamily: FONT, fontWeight: 600, fontSize: '14px', color: ACCENT_LT, textDecoration: 'none' }}>
                privacy@technofocus.ai
              </a>
            </div>
            <SubH>Additional Resources</SubH>
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0' }}>
              {[
                { label: 'Trust Center',              href: 'https://technofocus.ai/trust' },
                { label: 'Data Processing Addendum',  href: 'https://technofocus.ai/dpa' },
                { label: 'Sub-Processors',            href: 'https://technofocus.ai/sub-processors' },
                { label: 'Cookie Policy',             href: 'https://technofocus.ai/cookie-policy' },
              ].map(item => (
                <li key={item.label} style={{ marginBottom: '8px' }}>
                  <a href={item.href} target="_blank" rel="noopener noreferrer"
                    style={{ fontFamily: FONT, fontWeight: 500, fontSize: '14px', color: ACCENT_LT, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                  >
                    {item.label} <ExternalLink size={12} />
                  </a>
                </li>
              ))}
            </ul>
          </Section>

          {/* ── Section 20 ───────────────────────────────────────────── */}
          <Section number="20" title="Notice to European Users">
            <P>This section applies to users in the European Economic Area (EEA), UK, and Switzerland.</P>
            <SubH>Legal Basis for Processing</SubH>
            <P>We process personal data based on:</P>
            <Ul items={[
              'Contractual necessity (to provide Services)',
              'Legitimate interests (service improvement, security)',
              'Consent (where required, e.g., cookies, marketing)',
            ]} />
            <SubH>Your Rights</SubH>
            <P>You may request:</P>
            <Ul items={['Access', 'Correction', 'Deletion', 'Restriction', 'Portability', 'Objection', 'Withdrawal of consent']} />
            <SubH>Complaints</SubH>
            <P>You have the right to lodge a complaint with a supervisory authority.</P>
          </Section>

        </div>
      </div>

      <LevelUpFooter user={user} onLogout={onLogout} />
    </div>
  );
}
